import {
  useMutation as useMutationTanstack,
  type DefaultError,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

/**
 * Clé d'identité des variables d'une mutation : deux déclenchements qui
 * portent les mêmes variables sont le même geste (un double clic). Les
 * fichiers ne se sérialisent pas en JSON : on les distingue par nom, taille
 * et date. `null` si les variables ne se sérialisent pas — pas de garde alors.
 */
function cleDeVariables(variables: unknown): string | null {
  try {
    return (
      JSON.stringify(variables, (_cle, valeur: unknown) => {
        if (typeof File !== 'undefined' && valeur instanceof File)
          return `fichier:${valeur.name}:${String(valeur.size)}:${String(valeur.lastModified)}`;
        if (typeof Blob !== 'undefined' && valeur instanceof Blob)
          return `blob:${valeur.type}:${String(valeur.size)}`;
        if (typeof FormData !== 'undefined' && valeur instanceof FormData)
          return [...valeur.entries()].map(([k, v]) => [
            k,
            typeof v === 'string' ? v : `${v.name}:${String(v.size)}:${String(v.lastModified)}`,
          ]);
        return valeur;
      }) ?? 'undefined'
    );
  } catch {
    return null;
  }
}

/**
 * `useMutation` de TanStack, avec un garde anti-doublon : tant qu'une
 * mutation est en vol, un second déclenchement de la MÊME instance avec les
 * mêmes variables ne lance pas d'appel et rend la promesse du premier. C'est
 * le garde de tous les boutons « Créer », « Déposer », « Confirmer »… : le
 * double clic (ou Entrée répétée) arrive avant que `isPending` ait redessiné
 * le bouton, et créait deux enregistrements. Des variables différentes
 * (archiver deux lignes l'une après l'autre) passent normalement.
 *
 * Toute mutation de la régie passe par ici, pas par `@tanstack/react-query`
 * (règle ESLint `no-restricted-imports`).
 */
export function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TOnMutateResult = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
  queryClient?: QueryClient,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const mutation = useMutationTanstack(options, queryClient);
  const enVol = useRef(new Map<string, Promise<TData>>());
  const { mutateAsync: mutateAsyncBrut } = mutation;

  const mutateAsync = useCallback(
    (
      variables: TVariables,
      optionsAppel?: Parameters<typeof mutateAsyncBrut>[1],
    ): Promise<TData> => {
      const cle = cleDeVariables(variables);
      if (cle === null) return mutateAsyncBrut(variables, optionsAppel);
      const existante = enVol.current.get(cle);
      if (existante) return existante;
      const promesse = mutateAsyncBrut(variables, optionsAppel).finally(() => {
        enVol.current.delete(cle);
      });
      enVol.current.set(cle, promesse);
      return promesse;
    },
    [mutateAsyncBrut],
  );

  const mutate = useCallback(
    (variables: TVariables, optionsAppel?: Parameters<typeof mutateAsyncBrut>[1]): void => {
      mutateAsync(variables, optionsAppel).catch(() => {
        /* l'erreur est portée par `mutation.error` et `onError`, comme avec `mutate` */
      });
    },
    [mutateAsync],
  );

  return { ...mutation, mutate, mutateAsync } as UseMutationResult<
    TData,
    TError,
    TVariables,
    TOnMutateResult
  >;
}
